import React from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import axios from "axios";
import { getStoredAuthToken } from "./services/authStorage";
import { notifyPageVisit } from "./services/visitNotifications";
import { resolveLegacyLearnHashRedirect } from "./utils/learnRoutes";
import { captureFriendReferralFromUrl } from "./utils/friendReferral";

const API_BASE_URL =
  process.env.REACT_APP_API_URL ||
  (typeof window !== "undefined" ? `/api` : "http://localhost:5001/api");
const CANONICAL_HOST =
  process.env.REACT_APP_CANONICAL_HOST || "juegoxima.com";
const CANONICAL_BYPASS_PARAM = "ximaHostFallback";

const maybeRedirectLegacyLearnHash = () => {
  if (typeof window === "undefined") return;

  const targetPath = resolveLegacyLearnHashRedirect(window.location.hash);
  if (!targetPath) return;
  window.location.replace(targetPath);
};

const maybeRedirectToCanonicalHost = () => {
  if (typeof window === "undefined") return;

  const currentHost = String(window.location.hostname || "").trim().toLowerCase();
  const canonicalHost = String(CANONICAL_HOST || "").trim().toLowerCase();
  if (!currentHost || !canonicalHost || currentHost === canonicalHost) return;

  const isLocalHost =
    currentHost === "localhost" ||
    currentHost === "127.0.0.1" ||
    currentHost.endsWith(".local");
  if (isLocalHost) return;

  const isXimaDomain =
    currentHost === "juegoxima.com" || currentHost.endsWith(".juegoxima.com");
  if (!isXimaDomain) return;

  const currentParams = new URLSearchParams(window.location.search || "");
  if (currentParams.get(CANONICAL_BYPASS_PARAM) === "1") return;

  const targetUrl = `https://${canonicalHost}${window.location.pathname}${window.location.search}${window.location.hash}`;
  window.location.replace(targetUrl);
};

axios.defaults.baseURL = API_BASE_URL;
const storedAuthToken = getStoredAuthToken();
if (storedAuthToken) {
  axios.defaults.headers["Authorization"] = storedAuthToken;
}
// Some screens drop the default Authorization header (socket session checks, guest
// flows); requests made afterwards in this tab still send the tab's own session.
axios.interceptors.request.use((config) => {
  const headers = config.headers || {};
  const current = typeof headers.get === "function" ? headers.get("Authorization") : headers.Authorization;
  if (!current) {
    const tabToken = getStoredAuthToken();
    if (tabToken) {
      if (typeof headers.set === "function") headers.set("Authorization", tabToken);
      else config.headers = { ...headers, Authorization: tabToken };
    }
  }
  return config;
});
captureFriendReferralFromUrl();
maybeRedirectLegacyLearnHash();
maybeRedirectToCanonicalHost();

if (!storedAuthToken) {
  notifyPageVisit({ eventType: "page_load" });
}

const app = (
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
const rootElement = document.getElementById("root");

if (rootElement?.hasChildNodes()) {
  ReactDOM.hydrateRoot(rootElement, app);
} else {
  const root = ReactDOM.createRoot(rootElement);
  root.render(app);
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
